/**
 * Offline session manager — CRUD on `OfflineCheckSession` in IndexedDB.
 * See design §3.6, §3.7, §5.4 and plan issue 5.1.
 *
 * Sessions are persisted per-interaction (not batched) so the data
 * survives tab close, app crash, and device restart per the epic's
 * data-safety guarantee. Each method returns the updated session
 * record so callers can pipe it back into Redux without re-reading.
 *
 * The module is gated by the master flag indirectly: `getOfflineDB()`
 * is fine to call regardless (opens a Workbox-style empty DB in
 * disabled tenants), but the consumers in §5.8 only invoke session
 * manager methods on the offline branch — there's no useful work to
 * do for an opted-out tenant. We do NOT add an explicit flag check
 * here because session-manager methods are only called on the
 * deliberately-chosen offline UI paths.
 *
 * Photos handling is intentionally minimal in this module: `addPhoto`
 * is exposed but the storage-quota gate that the design calls for
 * (§5.8.7) lives in the UI layer where it can show a warning before
 * the blob is even handed in. We expose the size check as a separate
 * helper.
 */

import { getOfflineDB, STORE_SESSIONS } from "./db";
import type {
    CachedLayer,
    OfflineCheckConfig,
    OfflineCheckSession,
    OfflineDefect,
    OfflineFruit,
    OfflinePhoto,
    OfflineSessionStatus,
    OfflineStageAllocation,
    ReplayIdMap,
} from "./types";

function newId(): string {
    // crypto.randomUUID() is available in modern browsers and in jsdom via
    // the polyfill in setupTests.js (which sets `self.crypto`).
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    // Fallback for ancient browsers / unusual test envs. Not as good as
    // randomUUID but adequate as an idempotency key — server-side
    // collision risk is negligible at our scale.
    return `s_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
}

async function getSession(id: string): Promise<OfflineCheckSession | undefined> {
    const db = await getOfflineDB();
    return db.get(STORE_SESSIONS, id);
}

async function putSession(session: OfflineCheckSession): Promise<OfflineCheckSession> {
    const db = await getOfflineDB();
    await db.put(STORE_SESSIONS, session);
    return session;
}

/**
 * Serialize session read-modify-write mutations.
 *
 * Every mutator below reads the session, mutates it in memory, and writes it
 * back. Those steps span awaits, so concurrent captures interleave: a burst of
 * debounced `postDefect`s — one per check-level defect, all firing within the
 * same tick when the inspector fills ~6 defect fields — would each read the
 * same base session and the last `putSession` would clobber the others,
 * silently dropping captured defects (exactly the offline intake symptom).
 *
 * Chaining every mutation through one promise makes each read-modify-write
 * atomic with respect to the others. Offline is single-flow (one active
 * session, one replay at a time), so a global chain costs nothing in practice.
 * The chain swallows errors so one rejected mutation doesn't wedge the rest.
 */
let mutationChain: Promise<unknown> = Promise.resolve();
function withMutationLock<T>(fn: () => Promise<T>): Promise<T> {
    const run = mutationChain.then(fn, fn);
    mutationChain = run.then(() => undefined, () => undefined);
    return run;
}

/**
 * Locked read-modify-write for the common mutator shape: load the session,
 * apply an in-place change, persist, and return the updated record. Throws if
 * the session doesn't exist (same contract the mutators had inline).
 */
async function mutateSession(
    sessionId: string,
    mutator: (session: OfflineCheckSession) => void,
): Promise<OfflineCheckSession> {
    return withMutationLock(async () => {
        const session = await getSession(sessionId);
        if (!session) throw new Error(`offline session ${sessionId} not found`);
        mutator(session);
        return putSession(session);
    });
}

/**
 * Create a new offline session and persist it. Returns the new session.
 *
 * `barcode` is the raw scanned value (we keep it for diagnostics + the
 * replay step that resolves it to a server layer_id).
 *
 * `cachedLayer` is what `lookupByBarcode()` returned. The id is kept
 * for verification at replay time — if the server resolves the barcode
 * to a different layer, that's a re-labelling and the design says to
 * warn but continue (§3.8 step 1).
 *
 * `checkConfig` carries the `idempotency_key` (UUID generated on the
 * device) and `config_hash` (the ETag value from the most recent
 * `GET /tenants/{tid}/configs/active` response — already an md5 hash
 * computed server-side, so we reuse it instead of re-hashing client-
 * side; see plan issue 5.1).
 */
export async function createSession(
    barcode: string,
    cachedLayer: CachedLayer,
    checkConfig: OfflineCheckConfig,
): Promise<OfflineCheckSession> {
    const session: OfflineCheckSession = {
        id: newId(),
        status: "collecting",
        created_at: Date.now(),
        barcode,
        cached_layer: {
            id: cachedLayer.id,
            label: cachedLayer.label,
            fruit_type: cachedLayer.fruit_type,
            type: cachedLayer.type,
            latest_location: cachedLayer.latest_location ?? null,
        },
        check_config: checkConfig,
        fruits: [],
        check_fields: {},
        check_defects: {},
        retrospective_group_matching_siblings: false,
        photos: [],
        replay_bookmark: 0,
        replay_id_map: { fruit_ids: {} },
    };
    return putSession(session);
}

/**
 * Append a new fruit to the session. Returns the placeholder ids the
 * UI needs to render and dispatch the synthetic Redux action.
 *
 * `temp_avocado_id` is intentionally `Date.now() + index` so it stays
 * a large positive integer that won't collide with server-issued ids
 * (which are auto-incremented from a small starting value). The
 * design notes this in §3.7.
 */
export async function addFruit(
    sessionId: string,
    unit_of_account = "fruit",
): Promise<{ session: OfflineCheckSession; temp_avocado_id: number; idempotency_key: string }> {
    return withMutationLock(async () => {
        const session = await getSession(sessionId);
        if (!session) throw new Error(`offline session ${sessionId} not found`);
        const tempId = Date.now() + session.fruits.length;
        const idempotency_key = newId();
        const newFruit: OfflineFruit = {
            idempotency_key,
            temp_avocado_id: tempId,
            created_at: new Date().toISOString(),
            unit_of_account,
            fields: {},
            defects: [],
        };
        session.fruits = [...session.fruits, newFruit];
        await putSession(session);
        return { session, temp_avocado_id: tempId, idempotency_key };
    });
}

/**
 * Append a copy of an existing session fruit — the offline counterpart of
 * `POST /avocados/{id}/duplicate/`. Mirrors the backend
 * `FruitsRepository.duplicate_fruit`, which copies the fruit's columns plus
 * its active defects and meta: here that is the source's captured `fields`
 * (fruit columns and meta alike arrive as `PATCH /avocados/{id}/` writes) and
 * `defects`. Photos are not copied, matching the backend. The copy gets its
 * own temp id, idempotency_key and created_at, so replay creates it as a
 * separate server fruit and then patches the copied fields onto it.
 */
export async function duplicateFruit(
    sessionId: string,
    sourceTempAvocadoId: number,
): Promise<{ session: OfflineCheckSession; temp_avocado_id: number; idempotency_key: string }> {
    return withMutationLock(async () => {
        const session = await getSession(sessionId);
        if (!session) throw new Error(`offline session ${sessionId} not found`);
        const source = session.fruits.find((f) => f.temp_avocado_id === sourceTempAvocadoId);
        if (!source) throw new Error(`fruit ${sourceTempAvocadoId} not found in offline session ${sessionId}`);
        // Same placeholder scheme as addFruit, but a duplicate can land in the
        // same millisecond as the fruit it copies — never reuse a taken id.
        const tempId = Math.max(
            Date.now() + session.fruits.length,
            ...session.fruits.map((f) => f.temp_avocado_id + 1),
        );
        const idempotency_key = newId();
        const newFruit: OfflineFruit = {
            idempotency_key,
            temp_avocado_id: tempId,
            created_at: new Date().toISOString(),
            unit_of_account: source.unit_of_account,
            // Shallow copies suffice: the mutators below replace, never mutate.
            fields: { ...source.fields },
            defects: [...source.defects],
        };
        session.fruits = [...session.fruits, newFruit];
        await putSession(session);
        return { session, temp_avocado_id: tempId, idempotency_key };
    });
}

/**
 * Set or overwrite a single field on a fruit. Last-write-wins; this
 * mirrors the existing online `PATCH /avocados/{id}` semantics that
 * the replay engine will eventually call (idempotent by construction).
 */
export async function updateFruitField(
    sessionId: string,
    tempAvocadoId: number,
    field: string,
    value: unknown,
): Promise<OfflineCheckSession> {
    return mutateSession(sessionId, (session) => {
        session.fruits = session.fruits.map((f) => {
            if (f.temp_avocado_id !== tempAvocadoId) return f;
            return { ...f, fields: { ...f.fields, [field]: value } };
        });
    });
}

/**
 * Replace the defects list for a fruit. Replays as
 * `POST /defects/register-bulk` with `unregister_others: true` so the
 * server-side state ends up matching the array exactly.
 */
export async function updateFruitDefects(
    sessionId: string,
    tempAvocadoId: number,
    defects: OfflineDefect[],
): Promise<OfflineCheckSession> {
    return mutateSession(sessionId, (session) => {
        session.fruits = session.fruits.map((f) => {
            if (f.temp_avocado_id !== tempAvocadoId) return f;
            return { ...f, defects };
        });
    });
}

/**
 * Set or overwrite a check-level field (notes, flag, …). Replays as
 * `PATCH /checks/{id}/`.
 */
export async function updateCheckField(
    sessionId: string,
    field: string,
    value: unknown,
): Promise<OfflineCheckSession> {
    return mutateSession(sessionId, (session) => {
        session.check_fields = { ...session.check_fields, [field]: value };
    });
}

/**
 * Set the frequency for a check-level defect — the visual/intake "defect
 * frequency" form, where the online flow registers each defect against the
 * check with `fruit_id: null`. Last-write-wins per defect_id (matching the
 * backend `register_defect` upsert). Replays as `POST /defects/register`
 * with the real check_id at step 6.
 */
export async function updateCheckDefect(
    sessionId: string,
    defectId: string,
    frequency: number,
): Promise<OfflineCheckSession> {
    return mutateSession(sessionId, (session) => {
        session.check_defects = { ...(session.check_defects ?? {}), [defectId]: frequency };
    });
}

/** Store whether replay should attach this check to all matching siblings. */
export async function setRetrospectiveGroupMatchingSiblings(
    sessionId: string,
    enabled: boolean,
    groupByAttributes?: string[],
): Promise<OfflineCheckSession> {
    return mutateSession(sessionId, (session) => {
        session.retrospective_group_matching_siblings = enabled;
        session.retrospective_group_by_attributes = groupByAttributes;
    });
}

/**
 * Persist the location the check is linked to into check_config. Called
 * from the offline start-check flow when the check type is
 * location-linked, so the replay `POST /checks/` body carries
 * `location_id` and the synced check ends up allocated to a location —
 * matching the online flow, where StartCheckForm sends it directly.
 */
export async function setCheckConfigLocationId(
    sessionId: string,
    locationId: string,
): Promise<OfflineCheckSession> {
    return mutateSession(sessionId, (session) => {
        session.check_config = { ...session.check_config, location_id: locationId };
    });
}

/**
 * Record the stage allocation the check type asks for at check start, for
 * the replay to perform (see `OfflineCheckSession.stage_allocation`).
 */
export async function setStageAllocation(
    sessionId: string,
    allocation: OfflineStageAllocation,
): Promise<OfflineCheckSession> {
    return mutateSession(sessionId, (session) => {
        session.stage_allocation = allocation;
    });
}

/**
 * Append a photo to the active session. Returns the updated session
 * plus the synthetic image record callers can splice into the
 * TanStack Query cache so the form re-renders. Caller is responsible
 * for checking storage quota first (see §5.8.7 / issue 5.8 and
 * `estimateStorageUsage()` below).
 *
 * The synthetic record's `image_id` is `Date.now() + offset` — large
 * positive int that never collides with server-issued image ids;
 * follows the same convention as `temp_avocado_id`.
 */
export async function addPhoto(
    sessionId: string,
    photo: OfflinePhoto,
): Promise<{ session: OfflineCheckSession; syntheticImage: { image_id: number; type: string; base64: string; url_webp: string; focusMode?: unknown } }> {
    return withMutationLock(async () => {
        const session = await getSession(sessionId);
        if (!session) throw new Error(`offline session ${sessionId} not found`);
        session.photos = [...session.photos, photo];
        const updated = await putSession(session);
        const syntheticImage = {
            image_id: Date.now() + session.photos.length,
            type: photo.type,
            base64: photo.base64,
            // Preview renderers read `url_webp` (server blob URL online); offline we
            // have no server URL, so expose the base64 data URL there too. A data URL
            // is a valid <img src>, and captures are already image/webp.
            url_webp: photo.base64,
            focusMode: photo.focusMode,
        };
        return { session: updated, syntheticImage };
    });
}

/** Synthetic image record, shaped like what the server's image endpoints return. */
export interface SyntheticImage {
    image_id: number;
    type: string;
    base64: string;
    url_webp: string;
    focusMode?: unknown;
}

/**
 * Synthetic image records for one target, derived from an already-loaded
 * session. Pure — callers that already hold a session use this to avoid one
 * IndexedDB read per fruit.
 */
export function sessionPhotosForTarget(
    session: OfflineCheckSession,
    target: "fruit" | "check",
    targetTempId: number,
): SyntheticImage[] {
    return session.photos
        .filter((p) => p.target === target && p.target_temp_id === targetTempId)
        .map((p, idx) => ({
            image_id: idx + 1, // ordinal; deterministic per session read
            type: p.type,
            base64: p.base64,
            // See addPhoto: previews read `url_webp`; offline that's the base64 data URL.
            url_webp: p.base64,
            focusMode: p.focusMode,
        }));
}

/**
 * Read all photos for a given target (fruit by temp_avocado_id, or
 * check). Returns synthetic image records compatible with what
 * `useFruitImages` / `useCheckImages` would otherwise fetch from the
 * server. Used by the offline branches of those hooks.
 */
export async function getSessionPhotosForTarget(
    sessionId: string,
    target: "fruit" | "check",
    targetTempId: number,
): Promise<SyntheticImage[]> {
    const session = await getSession(sessionId);
    if (!session) return [];
    return sessionPhotosForTarget(session, target, targetTempId);
}

/** Remove a photo from the session by its target + position. */
export async function removePhoto(
    sessionId: string,
    target: "fruit" | "check",
    targetTempId: number,
    indexAmongTarget: number,
): Promise<OfflineCheckSession> {
    return mutateSession(sessionId, (session) => {
        let seen = -1;
        session.photos = session.photos.filter((p) => {
            if (p.target !== target || p.target_temp_id !== targetTempId) return true;
            seen += 1;
            return seen !== indexAmongTarget;
        });
    });
}

/**
 * Transition the session into `ready_to_sync`. The connectivity
 * monitor / replay engine pick it up from there.
 */
export async function markReadyToSync(
    sessionId: string,
): Promise<OfflineCheckSession> {
    return mutateSession(sessionId, (session) => {
        session.status = "ready_to_sync";
    });
}

/**
 * Read all sessions in the given status. Used by:
 *   - the tenant-switch invalidation flow (counts pending),
 *   - the Phase 6 replay engine (picks up `ready_to_sync` rows on a
 *     transition or app start),
 *   - the IsOfflineBar component (rendering a banner per state).
 */
export async function getSessionsByStatus(
    status: OfflineSessionStatus,
): Promise<OfflineCheckSession[]> {
    const db = await getOfflineDB();
    const idx = db.transaction(STORE_SESSIONS).store.index("by-status");
    return idx.getAll(IDBKeyRange.only(status));
}

/**
 * Persist a replay bookmark advance. Called by the replay engine
 * BEFORE each step so an interrupted replay can resume from the right
 * step on next connectivity. `idMap` carries the partial set of
 * server-issued ids resolved so far.
 */
export async function updateReplayBookmark(
    sessionId: string,
    step: number,
    idMap: ReplayIdMap,
): Promise<OfflineCheckSession> {
    return mutateSession(sessionId, (session) => {
        session.replay_bookmark = step;
        session.replay_id_map = idMap;
    });
}

/**
 * Best-effort estimate of how full the IndexedDB origin is. Returns
 * `usage / quota` as a fraction (0..1), or null if the browser doesn't
 * expose the API. Used by the Phase 5.8 storage warning.
 */
export async function estimateStorageUsage(): Promise<number | null> {
    if (typeof navigator === "undefined" || !navigator.storage?.estimate) {
        return null;
    }
    const { usage, quota } = await navigator.storage.estimate();
    if (typeof usage !== "number" || typeof quota !== "number" || quota <= 0) {
        return null;
    }
    return usage / quota;
}

/** Delete a session (used after a successful sync — issue 6.4). */
export async function deleteSession(sessionId: string): Promise<void> {
    const db = await getOfflineDB();
    await db.delete(STORE_SESSIONS, sessionId);
}

/** Read a session by id, or undefined if not found. */
export async function getSessionById(
    sessionId: string,
): Promise<OfflineCheckSession | undefined> {
    return getSession(sessionId);
}

/**
 * Pointer to the offline session the user is *currently editing*. The
 * UI is a single-flow at a time; only one session can be the
 * "currently active" one. When the user submits, we mark it
 * ready_to_sync and clear the active pointer; when they scan the next
 * pallet offline, a new session takes the slot.
 *
 * Lives at module scope rather than in Redux because:
 *   - A handful of non-React code paths (the apiClient interceptor in
 *     issue 5.5) need to read it without a hook.
 *   - The lifetime is "between scan and submit", which doesn't survive
 *     reload anyway — replay state lives in IndexedDB.
 *
 * Survives reload only if the consumer re-sets it from IndexedDB after
 * mount (we don't bother — a reload mid-check is an explicit user
 * action and `ready_to_sync` sessions get picked up by the replay
 * engine independently).
 */
let activeSessionId: string | null = null;

export function getActiveSessionId(): string | null {
    return activeSessionId;
}

export function setActiveSessionId(id: string | null): void {
    activeSessionId = id;
}

/**
 * Resolve the active session, verifying its row still exists.
 *
 * `activeSessionId` outlives the row it points at: the replay engine
 * deletes the session once it syncs (step 10) and the pointer stays put,
 * so a plain `getActiveSessionId()` truthiness check keeps routing reads
 * and writes into IndexedDB long after the data is gone. Callers that
 * branch "offline session vs server" must use this instead — a null
 * return means "no live session; talk to the server".
 *
 * Clears the stale pointer as a side effect so subsequent synchronous
 * `getActiveSessionId()` readers (the apiClient capture interceptor, the
 * storage guards) stop seeing a dead id.
 */
export async function getActiveSession(): Promise<OfflineCheckSession | null> {
    if (!activeSessionId) return null;
    const session = await getSession(activeSessionId);
    if (!session) {
        activeSessionId = null;
        return null;
    }
    return session;
}

/** For tests only. */
export function _resetActiveSessionForTests(): void {
    activeSessionId = null;
}
